import { useTranslation } from 'react-i18next';
import { ReferralReceiptBadge } from '@/components/patient/ReferralReceiptBadge';
import { SorPicker, type PickedReceiver } from '@/components/patient/ReferralSorPicker';
import { Button } from '@/components/ui/Button';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import { receivesEpikriseKind, type EpikriseKind } from '@/lib/referral/epikrise';
import { FORM_HINT_TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { HEADING, SUB, type EpikriseOutboxRow } from '@/components/referrals/epikrisePanelShared';

/** Built epikriser for the referral: original vs kopi, Rettet, 'Hent XML', 'Ret epikrise', 'Send kopi'. */
export function EpikriseSentList({
  rows,
  kindLabel,
  busyId,
  correctDisabled,
  copyFor,
  copyPick,
  onCorrect,
  onToggleCopy,
  onCopyPick,
  onSendCopy,
  onDownload,
  onDownloadCompanion,
}: {
  rows: EpikriseOutboxRow[];
  kindLabel: (kind: EpikriseKind) => string;
  busyId: string | null;
  correctDisabled: boolean;
  copyFor: EpikriseOutboxRow | null;
  copyPick: PickedReceiver | null;
  onCorrect: (row: EpikriseOutboxRow) => void;
  onToggleCopy: (row: EpikriseOutboxRow) => void;
  onCopyPick: (pick: PickedReceiver | null) => void;
  onSendCopy: () => void;
  onDownload: (row: EpikriseOutboxRow) => void;
  onDownloadCompanion: (row: EpikriseOutboxRow, companion: { exchangeId: string; fileName: string }) => void;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <div className="border-t border-ds-line px-4 py-3">
      <div className={HEADING}>{t('referrals.epikrise_sent', 'Dannede epikriser')}</div>
      <ul className="mt-2 divide-y divide-ds-line">
        {rows.map((row) => {
          const original = row.copy_of_id ? rows.find((r) => r.id === row.copy_of_id) : null;
          return (
            <li key={row.id} className="py-2 text-sm" data-testid="epikrise-sent-row">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ds-text">
                  {kindLabel(row.kind)}{' '}
                  {row.letter_status_code === 'rettetbrev' && (
                    <span className="mr-1 rounded bg-ds-amber-tint px-1.5 py-0.5 text-xs font-medium text-ds-amber-ink">
                      {t('referrals.epikrise_rettet', 'Rettet')}
                    </span>
                  )}
                  {row.copy_of_id ? (
                    <span className="mr-1 rounded bg-ds-teal-tint px-1.5 py-0.5 text-xs font-medium text-ds-teal">
                      {t('referrals.epikrise_copy_badge', 'Kopi af {{envelope}}', { envelope: original?.envelope_identifier ?? '—' })}
                    </span>
                  ) : (
                    <span className="mr-1 rounded bg-ds-neutral-tint px-1.5 py-0.5 text-xs font-medium text-ds-text">
                      {t('referrals.epikrise_original_badge', 'Original')}
                    </span>
                  )}
                  · {row.receiver_organisation_name ?? row.receiver_ean} · EAN {row.receiver_ean} · {row.envelope_identifier} ·{' '}
                  {formatReferralDateTime(row.created_at)}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  <ReferralReceiptBadge status={row.status} />
                  {!row.copy_of_id && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={busyId === row.id || correctDisabled}
                        onClick={() => onCorrect(row)}
                      >
                        {t('referrals.epikrise_correct', 'Ret epikrise')}
                      </Button>
                      <Button type="button" size="sm" variant="secondary" disabled={busyId === row.id} onClick={() => onToggleCopy(row)}>
                        {t('referrals.epikrise_send_copy', 'Send kopi til anden modtager')}
                      </Button>
                    </>
                  )}
                  <Button type="button" size="sm" variant="secondary" disabled={busyId === row.id} onClick={() => onDownload(row)}>
                    {t('referrals.revisit_download', 'Hent XML')}
                  </Button>
                </span>
              </div>
              {row.companions.length > 0 && (
                <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="epikrise-sent-companions">
                  <span className={FORM_HINT_TYPE}>{t('referrals.epikrise_sent_attachments', 'Bilag (XBIN01, linket fra Reference/BIN):')}</span>
                  {row.companions.map((companion) => (
                    <Button
                      key={companion.exchangeId}
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busyId === row.id}
                      onClick={() => onDownloadCompanion(row, companion)}
                    >
                      {t('patients.hospital_referral.outbox_download_companion', 'Hent bilag')}: {companion.fileName}
                    </Button>
                  ))}
                </div>
              )}
              {copyFor?.id === row.id && (
                <div className={cn('mt-2', SUB)} data-testid="epikrise-copy-form">
                  <SorPicker
                    label={t('referrals.epikrise_copy_receiver', 'Kopimodtager (SOR)')}
                    fieldId={`epikrise-copy-${row.id}`}
                    selected={copyPick}
                    partyLabel={t('referrals.epikrise_cc_party', 'Kopimodtager')}
                    receives={null}
                    hide={(organisation) => !receivesEpikriseKind(row.kind, organisation) || organisation.ean === row.receiver_ean}
                    onSelect={(organisation) =>
                      onCopyPick({ organisation, identifier: organisation.identifier ?? '', chosenIdentifierCode: null })
                    }
                    onIdentifier={(identifier) => onCopyPick(copyPick ? { ...copyPick, identifier } : null)}
                    onIdentifierCode={(chosenIdentifierCode) => onCopyPick(copyPick ? { ...copyPick, chosenIdentifierCode } : null)}
                    onClear={() => onCopyPick(null)}
                  />
                  <p className={FORM_HINT_TYPE}>
                    {t(
                      'referrals.epikrise_copy_hint',
                      'Kopien er den samme epikrise med identiske modtager- og kopimodtagerfelter; kun kuvertmodtagerens lokationsnummer og kuvert-/brev-id er nye.',
                    )}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busyId === row.id}
                    onClick={onSendCopy}
                    data-testid="epikrise-copy-submit"
                  >
                    {t('referrals.epikrise_copy_submit', 'Dan kopi')}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p className={cn('mt-1', FORM_HINT_TYPE)}>
        {t('referrals.revisit_receipts_hint', 'Kvitteringer og VANS håndteres i patientens udbakke.')}
      </p>
    </div>
  );
}
